import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import i18next from "../../i18n";

import { useOrder, useJointOrder } from "../../hooks/useOrders";
import {
  useMyOfferOnOrder,
  useJointOrderOfferStatus,
} from "../../hooks/useOffers";
import { useNotification } from "../../context";
import { OrderResponse, OrderState, ORDER_TYPES } from "../../types";
import { MaterialType } from "../../types/material.type";
import { isBiddingClosed } from "../../utils/biddingTime";

interface UseOfferAccessGateArgs {
  id: string;
  multipleOrderId: string;
  isJointOrder: boolean;
}

/**
 * Owns the offer submission screen's data-fetch + access-gating concern:
 * loading the (possibly joint) order, checking whether the seller already
 * offered, and deciding whether the seller may see the submission form at
 * all (open/consultation state) or must be redirected away (already
 * offered, or the order is closed). Extracted from `Offer.tsx` (Task 5
 * finding 2) so the container stays a thin render shell over this hook +
 * `useOfferSubmission` (which owns offer COMPOSITION instead).
 *
 * Includes the Task-5 finding-1 render-race fix: `isAlreadyOffered*` is
 * derived synchronously from the same settled query data the redirect
 * effects below use, so the container can gate the submission form's
 * render on it directly instead of waiting an extra render for
 * `isRedirecting` state to catch up with the effect that sets it.
 */
export const useOfferAccessGate = ({
  id,
  multipleOrderId,
  isJointOrder,
}: UseOfferAccessGateArgs) => {
  const navigate = useNavigate();
  const { openNotification } = useNotification();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { data: order, isLoading: isLoadingOrder } = useOrder(id);
  const { data: jointOrder, isLoading: isLoadingJointOrder } = useJointOrder(
    id,
    isJointOrder
  );

  const { data: existingOffer, isLoading: isCheckingExistingOffer } =
    useMyOfferOnOrder(id, !isJointOrder); // Only check for single orders

  const { data: jointOrderOfferStatus, isLoading: isCheckingJointOfferStatus } =
    useJointOrderOfferStatus(multipleOrderId, isJointOrder);

  const orders: OrderResponse[] = useMemo(() => {
    if (isJointOrder && jointOrder) return jointOrder;
    if (!isJointOrder && order) return [order];
    return [];
  }, [order, jointOrder, isJointOrder]);

  const hasServiceMaterial = useMemo(
    () =>
      orders.some(
        (orderItem) =>
          orderItem.material.category.type === MaterialType.SERVICES
      ),
    [orders]
  );

  const isJointType = useMemo(
    () =>
      orders.some(
        (o) =>
          o.type === ORDER_TYPES.JOINT || o.type === ORDER_TYPES.INVERSE_JOINT
      ),
    [orders]
  );

  const shouldUseMultipleRoute = isJointOrder || isJointType;
  const derivedMultipleOrderId =
    multipleOrderId || orders[0]?.multipleOrder?.id || "";

  const isOrderOpen = useMemo(() => {
    const allowedStates = [OrderState.OPEN, "OPENED"];
    return (
      orders.length > 0 &&
      orders.every(
        (orderItem) =>
          allowedStates.includes(orderItem.state) && !isBiddingClosed(orderItem)
      )
    );
  }, [orders]);

  // During the consultation (Q&A) period the supplier can view the RFQ and ask
  // questions via chat, but cannot submit offers yet. Allow page access in this
  // state; offer submission stays gated on isOrderOpen.
  const isConsultation = useMemo(
    () =>
      orders.length > 0 &&
      orders.every((orderItem) => orderItem.state === OrderState.CONSULTATION),
    [orders]
  );

  const canAccessOrder = isOrderOpen || isConsultation;

  // FIX (Task-5 ruling 2): `GET /offer/order/{id}/me` returns a bare boolean
  // (`hasOfferForOrderByLoggedUser`), not an offer object. The old guard
  // `if (!existingOffer || !existingOffer.order) return;` could never redirect a
  // seller who already had an offer (`true.order` is `undefined`), so revisiting
  // the RFQ showed a blank submission form. The boolean is now handled correctly:
  // a seller who already offered is sent to their opportunities list (whose
  // "Offers made" tab surfaces their offer) with a notice. We can't deep-link to
  // `/offer/:id` here because `/me` carries no offer id — that would need a BE
  // change to the endpoint, out of this FE-only task's scope.
  useEffect(() => {
    if (isJointOrder) return; // Skip for joint orders - handled separately
    if (!existingOffer) return; // boolean false -> no offer yet -> show the form

    setIsRedirecting(true);
    openNotification(i18next.t("tenders.seller.alreadyOffered"));
    navigate("/biddings");
  }, [existingOffer, isJointOrder, navigate, openNotification]);

  // For JOINT orders: redirect only if ALL offers exist
  useEffect(() => {
    if (!isJointOrder) return; // Skip for single orders
    if (!jointOrderOfferStatus) return;

    if (
      jointOrderOfferStatus.ordersWithoutOffers.length === 0 &&
      jointOrderOfferStatus.ordersWithOffers.length > 0
    ) {
      setIsRedirecting(true);
      openNotification(i18next.t("tenders.seller.alreadyOffered"));
      const firstOffer = jointOrderOfferStatus.ordersWithOffers[0];
      navigate(
        `/offer/multiple/${firstOffer.orderId}?multipleOrderId=${multipleOrderId}`
      );
    }
  }, [
    jointOrderOfferStatus,
    isJointOrder,
    multipleOrderId,
    navigate,
    openNotification,
  ]);

  const isLoading =
    isLoadingOrder ||
    isLoadingJointOrder ||
    isCheckingExistingOffer ||
    isCheckingJointOfferStatus;

  useEffect(() => {
    if (isLoading || !orders.length) return;

    if (!isJointOrder && existingOffer) return;
    if (isJointOrder && jointOrderOfferStatus?.ordersWithoutOffers.length === 0)
      return;
    if (canAccessOrder) return;

    setIsRedirecting(true);
    openNotification(i18next.t("tenders.seller.closedOrder"));
    navigate("/biddings");
  }, [
    existingOffer,
    isJointOrder,
    isLoading,
    jointOrderOfferStatus,
    canAccessOrder,
    navigate,
    openNotification,
    orders.length,
  ]);

  // RENDER-RACE FIX (Task 5 follow-up, finding 1): once the already-offered
  // checks resolve, the redirect effects above only take effect on the NEXT
  // render (setIsRedirecting/navigate run after commit) — so the render that
  // immediately follows `isLoading` flipping to `false` would otherwise fall
  // through to the blank submission form for exactly one paint before the
  // effect fires. Compute the "already offered" verdict synchronously from
  // the same settled query data the effects use, so the container can gate
  // on it directly instead of waiting for `isRedirecting` to catch up.
  const isAlreadyOfferedSingle = !isJointOrder && !!existingOffer;
  const isAlreadyOfferedJoint =
    isJointOrder &&
    !!jointOrderOfferStatus &&
    jointOrderOfferStatus.ordersWithoutOffers.length === 0 &&
    jointOrderOfferStatus.ordersWithOffers.length > 0;

  return {
    order,
    jointOrder,
    orders,
    hasServiceMaterial,
    isJointType,
    shouldUseMultipleRoute,
    derivedMultipleOrderId,
    isOrderOpen,
    isConsultation,
    canAccessOrder,
    jointOrderOfferStatus,
    isLoading,
    isRedirecting:
      isRedirecting || isAlreadyOfferedSingle || isAlreadyOfferedJoint,
  };
};
